import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { customFieldsApi, CustomFieldDefinition } from '../api/customFields';

interface CustomFieldsRendererProps {
  entityType: 'lead' | 'account' | 'contact';
  entityId?: number;
  values: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
}

const CustomFieldsRenderer: React.FC<CustomFieldsRendererProps> = ({
  entityType,
  entityId,
  values,
  onChange,
}) => {
  const { data: fieldDefinitions = [], isLoading } = useQuery({
    queryKey: ['custom-fields', entityType],
    queryFn: () => customFieldsApi.getDefinitions(entityType),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-10 bg-gray-200 rounded"></div>
        <div className="animate-pulse h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (fieldDefinitions.length === 0) {
    return null;
  }

  const renderField = (field: CustomFieldDefinition) => {
    const fieldValue = values[field.field_name] || field.default_value || '';
    const fieldId = `custom-field-${field.id}`;

    switch (field.field_type) {
      case 'text':
        return (
          <div key={field.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              id={fieldId}
              value={fieldValue}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className="input-premium"
              required={field.is_required}
              placeholder={field.label}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              id={fieldId}
              value={fieldValue}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className="input-premium"
              required={field.is_required}
              rows={3}
              placeholder={field.label}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              id={fieldId}
              value={fieldValue}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className="input-premium"
              required={field.is_required}
              placeholder={field.label}
            />
          </div>
        );

      case 'date':
        return (
          <div key={field.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              id={fieldId}
              value={fieldValue}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className="input-premium"
              required={field.is_required}
            />
          </div>
        );

      case 'boolean':
        return (
          <div key={field.id} className="flex items-center">
            <input
              type="checkbox"
              id={fieldId}
              checked={fieldValue === true || fieldValue === 'true' || fieldValue === '1'}
              onChange={(e) => onChange(field.field_name, e.target.checked)}
              className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              required={field.is_required}
            />
            <label htmlFor={fieldId} className="text-sm font-semibold text-gray-700">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
          </div>
        );

      case 'select':
        return (
          <div key={field.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              id={fieldId}
              value={fieldValue}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className="input-premium"
              required={field.is_required}
            >
              <option value="">Select {field.label}...</option>
              {field.options?.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      default:
        return (
          <div key={field.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              id={fieldId}
              value={fieldValue}
              onChange={(e) => onChange(field.field_name, e.target.value)}
              className="input-premium"
              required={field.is_required}
              placeholder={field.label}
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-4 pt-4 border-t border-gray-200">
      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Custom Fields</h3>
      {fieldDefinitions.map(renderField)}
    </div>
  );
};

export default CustomFieldsRenderer;
